// 商品レビュー一覧ウィジェット。
//
// 現行のレビュー表示は2系統あり、どちらもこのウィジェットへ一本化する（Issue #27）:
// - C系統: OppSites Views/Review/* + Controllers/ReviewController.cs（HTML断片）。
//   fukuro.in など5サイト。管理画面の「適用+更新」(ArticlePostModel.ReviewUpdate) が
//   この HTML をフリーページ本文へ静的に書き込んでいる
// - A系統: wuSiteResource2 Shared/Review.ts（AngularJS）+ api.workup-system.com の
//   /api/Site/{id}/ProductReview。paper-straw など5サイトの「お客様の声」ページが読んでいる。
//   ★以前ここに「実ページで使われていない」と書いてあったのは誤り。2026-08-30 にその判断で
//   Review.ts を消し、5サイトのページが9日間壊れた（Issue #27 の経緯）
//
// 店側の書き方:
//   <div data-wu-widget="product-review" data-site="1"></div>
//   data-page-size="5" で初回の件数を変えられる（商品ページの小さな枠向け。既定 20、上限は API の 20）。
//   続きを読むは常に 5 件ずつ。
//
// ★対象は顔画像の置き場所が分かっているサイトのみ（下の SITE_IMAGES）。
//   それ以外の siteId では何も表示しない（現行 C系統は KeyNotFoundException で例外落ちしていたが、
//   ウィジェットは黙って何もしないほうが安全）。
//
// 落とした挙動（意図的な範囲縮小。実害が無いと判断したもの）:
// - ReviewCounters への閲覧数カウント（「続きを読む」を押すたびの UPDATE）。
//   分析用の副作用で、客への表示には関わらない。このリポジトリは書き込みを
//   用途限定にする方針のため、移植しない（2026-09-08 決定）
// - A系統の顔画像の選び方（Id % 5）。C系統と同じくページ内で face1〜5 を順に振る
// - A系統の「続きを読む」画像ボタン。画像は使わず、同じ見た目（枠線つき・右寄せ）を CSS で作る（Issue #50）

import type { WidgetContext } from '../../shared/registry'
import { injectStyleOnce } from '../../shared/style'
import { css } from './style'
import { fetchReviews, type ProductReviewCursor, type ProductReviewItem } from './api'

const DEFAULT_API = 'https://front.workup-system.com/api/product-review'

const FIRST_PAGE_SIZE = 20
const MORE_PAGE_SIZE = 5
const FACE_COUNT = 5

// サイトごとの画像の置き場所。face は `${face}1.jpg`〜`${face}5.jpg`、newBadge は「新着」バッジ。
// 出どころ:
// - 1/2/3/4/6: 現行 ReviewController.GetImageUrl と同じ（C系統）
// - 9/10/11/14/15/16: 各店の「お客様の声」ページ本文に直書きされていた ng-src を写した（A系統。
//   2026-09-08 に全ページを読んで確定）。paper-straw だけ別ホストの rakurakk.sakura.ne.jp にあり、
//   自店の data/paper-straw/image/face1.jpg は 404。A系統に新着バッジは無く、review_new.gif も
//   置かれていない（実測 404）ので null
// - 8（閉鎖済み）は入れない
type SiteImages = { face: string; newBadge: string | null }

const SITE_IMAGES: Record<number, SiteImages> = {
  1: { face: '//www.fukuro.in/data/opp-fukuro/image/face', newBadge: '//www.fukuro.in/data/opp-fukuro/image/review_new.gif' },
  2: { face: '//www.anshinya.com/data/emask/image/face', newBadge: '//www.anshinya.com/data/emask/image/review_new.gif' },
  3: { face: 'https://www.opp-honpo.net/data/opp-honpo/image/face', newBadge: 'https://www.opp-honpo.net/data/opp-honpo/image/review_new.gif' },
  4: { face: '//daishi.ocnk.net/data/daishi/image/face', newBadge: '//daishi.ocnk.net/data/daishi/image/review_new.gif' },
  6: { face: '//www.pack-japan.jp/data/pack-japan/image/face', newBadge: '//www.pack-japan.jp/data/pack-japan/image/review_new.gif' },
  9: { face: 'https://rakurakk.sakura.ne.jp/paper-straw.jp/common/face', newBadge: null },
  10: { face: 'https://www.b-futou.com/data/b-futou/image/face', newBadge: null },
  11: { face: 'https://www.comic-samurai.com/data/comic-samurai/image/face', newBadge: null },
  14: { face: 'https://www.kirakirapunpun-maru.com/data/kirakirapunpun/image/face', newBadge: null },
  15: { face: 'https://www.button-mart.com/data/button/image/face', newBadge: null },
  16: { face: 'https://www.lustia.net/data/lustia/image/face', newBadge: null },
}

/** data-page-size を 1〜20 の整数に丸める。無い・不正なら fallback。 */
function clampPageSize(raw: string | undefined, fallback: number): number {
  const n = Number(raw)
  if (!raw || !Number.isInteger(n) || n < 1) return fallback
  return Math.min(n, FIRST_PAGE_SIZE)
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
}

/** today からの日数。2日未満なら「新着」バッジを出す（現行 (today - EntryDate).TotalDays < 2 と同じ）。 */
function isNew(entryDate: string, today: Date): boolean {
  const d = new Date(entryDate + 'T00:00:00Z')
  const base = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
  return (base.getTime() - d.getTime()) / 86400000 < 2
}

function renderItem(item: ProductReviewItem, faceIdx: number, images: SiteImages, today: Date): string {
  const products = item.products
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' / ')
  const newBadge =
    images.newBadge && isNew(item.entryDate, today)
      ? `<div class="voice-new"><img src="${images.newBadge}" alt="新しいレビュー" /></div>`
      : ''
  return (
    `<div class="voice-box">${newBadge}` +
    `<div class="face"><img src="${images.face}${faceIdx}.jpg" alt="お客様の声" /></div>` +
    `<div class="voice-content">` +
    `<div class="date">${esc(item.entryDate)}</div>` +
    `<div class="products-label">購入商品<span style="color:black;">：</span></div>` +
    `<div class="products">${esc(products)}</div>` +
    // body は意図的にエスケープしない（現行 @Html.Raw(entity.Body) と同じ）。
    // 出所は管理画面から入力される社内管理下のレビュー本文で、客からの入力ではない。
    `<div class="voice">${item.body}</div>` +
    `</div></div>`
  )
}

function productReview(el: HTMLElement, ctx: WidgetContext): void {
  const images = SITE_IMAGES[ctx.siteId]
  if (!images) return

  injectStyleOnce('product-review', css)

  const api = el.dataset.api || DEFAULT_API
  const today = new Date()
  const firstPageSize = clampPageSize(el.dataset.pageSize, FIRST_PAGE_SIZE)

  el.classList.add('wu-review')
  const list = document.createElement('div')
  el.appendChild(list)

  const moreWrap = document.createElement('div')
  moreWrap.className = 'wu-more'
  const moreBtn = document.createElement('button')
  moreBtn.type = 'button'
  moreBtn.textContent = '続きを読む'
  moreWrap.appendChild(moreBtn)

  // Id 単独では EntryDate の並びと食い違う行を拾い損ねるため、複合カーソルを持つ。
  let cursor: ProductReviewCursor | null = null
  let loading = false

  // 現行はAjaxで受け取るページ1つの中で face1〜5 を振り直している（跨いで連続させない）。
  function appendItems(items: ProductReviewItem[]): void {
    let faceIdx = 1
    for (const item of items) {
      list.insertAdjacentHTML('beforeend', renderItem(item, faceIdx, images, today))
      faceIdx = faceIdx === FACE_COUNT ? 1 : faceIdx + 1
      cursor = { id: item.id, entryDate: item.entryDate }
    }
  }

  async function load(pageSize: number): Promise<void> {
    if (loading) return
    loading = true
    moreBtn.disabled = true
    try {
      const res = await fetchReviews(api, ctx.siteId, cursor, pageSize)
      appendItems(res.items)
      if (res.hasMore) {
        el.appendChild(moreWrap)
      } else {
        moreWrap.remove()
      }
    } catch (e) {
      console.error('[wu] product-review の取得に失敗しました', e)
    } finally {
      loading = false
      moreBtn.disabled = false
    }
  }

  moreBtn.addEventListener('click', () => load(MORE_PAGE_SIZE))

  load(firstPageSize)
}

export default productReview
